import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { ExtededImageDetails } from "../../../../../images/interfaces";
import * as apiUrls from "config/apiUrls";
import { ImageSize } from "constants/imageSizes";
import { SiteArticleSocialShare1280 } from "../articleSocialShare1280Component/siteArticleSocialShare1280";

interface GalleryProps {
    isLTR: boolean
    currentImageIndex: number
    images: ExtededImageDetails[]
    close: () => void
    swipeLeft: () => void
    swipeRight: () => void
}

export const ArticleGalleryView = (props: GalleryProps) => {
    const { isLTR, currentImageIndex, images, close, swipeLeft, swipeRight } = props;
    const [isShareBtnsVisible, setIsShareBtnsVisible] = useState(false);
    const [imageAnimationStyle, setImageAnimationStyle] = useState<React.CSSProperties | undefined>({});
    const [isClosing, setIsClosing] = useState(false);
    const displayedImageRef = useRef<HTMLImageElement>(null);
    const animationFrameRef: any = useRef<number>();
    const popUpCloseButtonRef: any = useRef<HTMLButtonElement>();
    const focusableElsRef: any = useRef<NodeListOf<HTMLElement>>();
    const ltrClassName = isLTR ? "englishSite" : "";
    const imageUrl = images[currentImageIndex] ? apiUrls.getImageUrl(images[currentImageIndex], { imageSize: ImageSize.xLarge }) : "";
    const caption = images[currentImageIndex] && images[currentImageIndex].caption && images[currentImageIndex].caption.trim();
    const credit = images[currentImageIndex] && images[currentImageIndex].credit && images[currentImageIndex].credit.trim();

    const handleKeyPress = (e: KeyboardEvent): void => {
        if (isClosing) return;
        if (e.key === "Escape") { close() }
        if (e.key === "ArrowLeft") { swipeLeft() }
        if (e.key === "ArrowRight") { swipeRight() }
    }

    const handleShareBtnClicked = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsShareBtnsVisible(!isShareBtnsVisible);
    }

    const handleCloseGallery = (e: any) => {
        if (isClosing) return;
        e.stopPropagation();
        close();
    }

    const handleWheel = (e: React.WheelEvent) => {
        if (isClosing) return;
        setIsClosing(true);

        const currentImage = images[currentImageIndex];
        const target = displayedImageRef.current;

        if (!currentImage || !target) {
            close();
            return;
        }

        const originImageElementId = `image_${currentImage.imageId}`;
        const originElement = document.getElementById(originImageElementId);

        if (!originElement) {
            close();
            return;
        }

        const originRect = originElement.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        const translateX = originRect.left - targetRect.left;
        const translateY = originRect.top - targetRect.top;
        const scaleX = originRect.width / targetRect.width;
        const scaleY = originRect.height / targetRect.height;

        const distance = Math.sqrt(translateX * translateX + translateY * translateY);
        const viewportDiagonal = Math.sqrt(window.innerWidth * window.innerWidth + window.innerHeight * window.innerHeight);
        const isInView = originRect.bottom > 0 && originRect.top < window.innerHeight &&
            originRect.right > 0 && originRect.left < window.innerWidth;
        const baseDuration = 500;
        const maxExtraDuration = 1200;
        const animationDuration = isInView ? baseDuration : Math.round(baseDuration + maxExtraDuration * Math.min(distance / viewportDiagonal, 1));

        setImageAnimationStyle({
            transform: `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`,
            transformOrigin: "top left",
            transition: `transform ${animationDuration}ms cubic-bezier(0.2, 0, 0.1, 1)`,
            willChange: "transform"
        });

        setTimeout(() => {
            close();
        }, animationDuration);
    }

    useLayoutEffect(() => {
        if (typeof window === "undefined") return;

        const currentImage = images[currentImageIndex];
        const target = displayedImageRef.current;

        if (!currentImage || !target) return;

        const originImageElementId = `image_${currentImage.imageId}`;
        const originElement = document.getElementById(originImageElementId);

        if (!originElement) {
            setImageAnimationStyle(undefined);
            return;
        }

        const animateImage = () => {
            const originRect = originElement.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();

            if (!targetRect.width || !targetRect.height) { return; }

            const translateX = originRect.left - targetRect.left;
            const translateY = originRect.top - targetRect.top;
            const scaleX = originRect.width / targetRect.width;
            const scaleY = originRect.height / targetRect.height;

            setImageAnimationStyle({
                transform: `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`,
                transformOrigin: "top left",
                transition: "none",
                willChange: "transform"
            });

            animationFrameRef.current = window.requestAnimationFrame(() => {
                setImageAnimationStyle({
                    transform: "",
                    transformOrigin: "top left",
                    transition: "transform 500ms cubic-bezier(0.2, 0, 0.1, 1)",
                    willChange: "transform"
                });
            });
        };

        if (animationFrameRef.current) {
            window.cancelAnimationFrame(animationFrameRef.current);
        }

        if (target.complete && target.naturalWidth) {
            animateImage();
            return;
        }

        const handleImageLoad = () => {
            animateImage();
        };

        target.addEventListener("load", handleImageLoad, { once: true });

        return () => {
            target.removeEventListener("load", handleImageLoad);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                window.cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const galleryElement = document.getElementById('images_gallery');
        if (galleryElement) {
            focusableElsRef.current = galleryElement.querySelectorAll('input:not([disabled]), a[href], area[href], button:not([disabled]), [tabindex="0"], iframe[src]');
        }
        document.addEventListener("keydown", handleKeyPress);

        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [handleKeyPress]);

    useEffect(() => {
        if (popUpCloseButtonRef.current) {
            popUpCloseButtonRef.current.focus();
        }
    }, [currentImageIndex]);

    return (
        <div className={`articleImageGalleryDesktop${isClosing ? " closing" : ""}`} key={"articleImageGalleryDesktop"} role="dialog" aria-label="images gallery" id="images_gallery" onClick={isClosing ? undefined : close} onWheel={handleWheel} tabIndex={0}>
            <div className="GalleryWrapper">
                <button className={`closeBtn ${ltrClassName}`} title={__("Close")} aria-label={__("Close")} onClick={handleCloseGallery} ref={popUpCloseButtonRef as any}></button>

                <div className="image-wrapper">
                    <img src={imageUrl} alt={images[currentImageIndex].alt} ref={displayedImageRef as any} style={imageAnimationStyle} />
                </div>

                <div className={`bottom-container ${ltrClassName}`} onClick={e => e.stopPropagation()}>
                    <div className="imageInfo">
                        {caption && <div className="caption">{caption}</div>}

                        {credit && <div className="credit">({credit})</div>}
                    </div>

                    {images.length > 1 &&
                        <div className="images-counter">
                            <button className={`navigationArrow ${"swipeLeft"}`} title="prev" aria-label="prev" onClick={swipeLeft} />

                            <div className="counter-digits">
                                <span className="current">{currentImageIndex + 1}</span>
                                /
                                <span className="totalNumOfImages">{images.length}</span>
                            </div>

                            <button className={`navigationArrow ${"swipeRight"}`} title="next" aria-label="next" onClick={swipeRight} />
                        </div>
                    }

                    <div className="shareOptions" onClick={handleShareBtnClicked}></div>

                    {isShareBtnsVisible && <SiteArticleSocialShare1280 className={"articleImageGalleryDesktopShareBtns"} language={isLTR ? "en" : "he"} showBtns={["wa", "fb", "mailto", "copyLink"]} />}
                </div>
            </div>
        </div >
    )
}
